import React, { useState, useEffect, useCallback } from 'react';
import { SearchIcon, ArrowDown } from '../../../public/icons';
import CollectionItem from './CollectionItem';
import Pagination from '../../shared/Pagination';
import { useToast } from '../../../context/ToastContext';
import adminCollectionService from '../../../services/admin/admin-collectionService';

const CollectionsTab = ({ refreshTrigger }) => {
  const [collections, setCollections] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('All Status');
  const [pagination, setPagination] = useState({
    currentPage: 1,
    itemsPerPage: 10,
    totalPages: 1,
    totalItems: 0
  });

  const { success, error } = useToast();

  const statusOptions = ['All Status', 'active', 'draft', 'inactive', 'archived'];

  // ============ FETCH ALL COLLECTIONS ============
// ============ FETCH ALL COLLECTIONS ============
const fetchCollections = useCallback(async (page = 1) => {
  try {
    setLoading(true);

    console.log('🔄 [CollectionsTab] Fetching all collections...');

    const params = {
      page,
      limit: pagination.itemsPerPage,
      search: searchQuery,
      status: statusFilter !== 'All Status' ? statusFilter : undefined,
    };

    const response = await adminCollectionService.getAllCollections(params);
    
    if (response.success) {
      console.log('✅ [CollectionsTab] Collections fetched:', response.data.collections);
      
      // ✅ FIXED: REMOVE THE FILTER! Show ALL collections, not just parents
      const allCollections = response.data.collections.map(collection => ({
        id: collection.id,
        name: collection.name || 'Unnamed Collection',
        type: collection.type || 'category',
        description: collection.description || '',
        collectionType: collection.collectionType,
        inventory: collection.inventory || '0 Items',
        stockQuantity: collection.stockQuantity || 0,
        price: collection.price || 0,
        basePrice: collection.basePrice || 0,
        status: collection.status || 'Draft',
        parentId: collection.parentId,
        children: collection.children || [],
        isExpanded: false,
        items: collection.children || [],
        hasChildren: collection.hasChildren || collection.isExpandable || false,
        subCollectionsCount: collection.subCollectionsCount || 0,
        standaloneCount: collection.standaloneCount || 0,
        coverImage: collection.coverImage,
        sortOrder: collection.sortOrder || 0,
        productId: collection.productId,
        productName: collection.productName,
        createdAt: collection.createdAt
      }));
      
      setCollections(allCollections);
      setPagination(prev => ({
        ...prev,
        currentPage: response.data.pagination?.page || 1,
        totalPages: response.data.pagination?.pages || 1,
        totalItems: response.data.pagination?.total || 0
      }));
    } else {
      console.error('❌ [CollectionsTab] Failed to fetch collections:', response.message);
      error(response.message || 'Failed to fetch collections');
      setCollections([]);
    }
  } catch (err) {
    console.error('💥 [CollectionsTab] Error fetching collections:', err);
    error('Unable to load collections. Please check your connection.');
    setCollections([]);
  } finally {
    setLoading(false);
  }
}, [searchQuery, statusFilter, pagination.itemsPerPage, error]);
  // ============ FETCH CHILDREN FOR A COLLECTION ============
  const fetchChildren = async (collectionId, collectionType) => {
    try {
      console.log(`🔄 [CollectionsTab] Fetching children for ${collectionType}:`, collectionId);
      
      let children = [];
      
      if (collectionType === 'parent') {
        // Fetch sub-collections under this parent
        const subResponse = await adminCollectionService.getSubCollectionsByParent(collectionId, {
          status: 'active,draft'
        });
        
       if (subResponse.success) {
  children = subResponse.data.collections.map(sub => ({
    id: sub.id,
    name: sub.name || 'Unnamed Sub Collection',
    type: sub.type || 'category',
    description: sub.description || '',
    collectionType: 'sub',
    inventory: sub.inventory || '0 Items',
    stockQuantity: sub.stockQuantity || 0,
    price: sub.price || 0,
    basePrice: sub.basePrice || 0,
    status: sub.status || 'Draft',
    parentId: collectionId,
    children: [],
    isExpanded: false,
    items: [],
    // ✅ ADD THIS: Check if sub-collection has standalone products
    hasChildren: sub.childCount > 0 || 
                 (sub.childCollections?.standaloneProducts?.length > 0),
    standaloneCount: sub.childCollections?.standaloneProducts?.length || 0,
    coverImage: sub.coverImage,
    sortOrder: sub.sortOrder || 0,
    productId: sub.productId,
    productName: sub.productName
  }));
}
      } else if (collectionType === 'sub') {
        // Fetch standalone products under this sub-collection
        const standaloneResponse = await adminCollectionService.getStandaloneCollections({
          parentId: collectionId,
          status: 'active,draft'
        });
        
        if (standaloneResponse.success) {
          children = standaloneResponse.data.collections.map(standalone => ({
            id: standalone.id,
            name: standalone.name || 'Unnamed Product',
            type: standalone.type || 'category',
            description: standalone.description || '',
            collectionType: 'standalone',
            inventory: `${standalone.stockQuantity || 0} in stock`,
            stockQuantity: standalone.stockQuantity || 0,
            price: standalone.basePrice || 0,
            basePrice: standalone.basePrice || 0,
            status: standalone.status || 'Draft',
            parentId: collectionId,
            children: [],
            isExpanded: false,
            items: [],
            coverImage: standalone.coverImage,
            sortOrder: standalone.sortOrder || 0,
            productId: standalone.productId,
            productName: standalone.productName
          }));
        }
      }
      
      return children;
    } catch (err) {
      console.error(`💥 [CollectionsTab] Error fetching children for ${collectionId}:`, err);
      return [];
    }
  };

  // ============ FIXED: Handle toggle expand for parent collections ============
  const handleToggleExpand = async (collectionId, collectionType) => {
    // Find the collection to expand
    const collection = collections.find(c => c.id === collectionId);
    if (!collection) return;

    // If already expanded, just toggle
    if (collection.isExpanded) {
      setCollections(prevCollections =>
        prevCollections.map(c =>
          c.id === collectionId ? { ...c, isExpanded: false } : c
        )
      );
      return;
    }

    // If expanding and no children loaded yet, fetch them
    if (!collection.isExpanded && collection.children.length === 0) {
      const children = await fetchChildren(collectionId, collectionType);
      
      setCollections(prevCollections =>
        prevCollections.map(c =>
          c.id === collectionId 
            ? { 
                ...c, 
                isExpanded: true,
                children: children,
                items: children
              } 
            : c
        )
      );
    } else {
      // Just expand without fetching
      setCollections(prevCollections =>
        prevCollections.map(c =>
          c.id === collectionId ? { ...c, isExpanded: true } : c
        )
      );
    }
  };

  // ============ FIXED: Handle toggle expand for sub collections ============
  const handleSubToggleExpand = async (collectionId, collectionType, parentId) => {
    setCollections(prevCollections => {
      return prevCollections.map(parent => {
        if (parent.id === parentId) {
          // Find the sub-collection to expand
          const subCollection = parent.children.find(child => child.id === collectionId);
          
          if (subCollection && !subCollection.isExpanded && subCollection.children.length === 0) {
            // Fetch children asynchronously and then update
            fetchChildren(collectionId, 'sub').then(children => {
              setCollections(currentCollections => {
                return currentCollections.map(p => {
                  if (p.id === parentId) {
                    return {
                      ...p,
                      children: p.children.map(child => 
                        child.id === collectionId 
                          ? { ...child, isExpanded: true, children, items: children }
                          : child
                      )
                    };
                  }
                  return p;
                });
              });
            });
            
            // Return parent with loading state
            return {
              ...parent,
              children: parent.children.map(child =>
                child.id === collectionId
                  ? { ...child, isExpanded: true } // Show expanded immediately
                  : child
              )
            };
          } else {
            // Just toggle expansion state
            return {
              ...parent,
              children: parent.children.map(child =>
                child.id === collectionId
                  ? { ...child, isExpanded: !child.isExpanded }
                  : child
              )
            };
          }
        }
        return parent;
      });
    });
  };

  const handleCollectionAction = async (collectionId, action) => {
    try {
      switch (action) {
        case 'edit':
          console.log('Edit collection:', collectionId);
          break;
        
        case 'toggle-status':
          const collection = collections.find(c => c.id === collectionId);
          const newStatus = collection.status === 'Active' ? 'Inactive' : 'Active';
          
          const response = await adminCollectionService.updateCollectionStatus(collectionId, newStatus.toLowerCase());
          
          if (response.success) {
            success(`Collection status updated to ${newStatus}`);
            fetchCollections(pagination.currentPage);
          } else {
            error(response.message || 'Failed to update status');
          }
          break;
        
        case 'delete':
          if (window.confirm('Are you sure you want to delete this collection?')) {
            const deleteResponse = await adminCollectionService.deleteCollection(collectionId);
            
            if (deleteResponse.success) {
              success('Collection deleted successfully');
              fetchCollections(pagination.currentPage);
            } else {
              error(deleteResponse.message || 'Failed to delete collection');
            }
          }
          break;
        
        default:
          console.log('Action menu opened for:', collectionId);
      }
    } catch (err) {
      console.error('Collection action error:', err);
      error('Failed to perform action. Please try again.');
    }
  };

  const handlePageChange = (pageNumber) => {
    setPagination(prev => ({ ...prev, currentPage: pageNumber }));
    fetchCollections(pageNumber);
  };

  const filteredCollections = collections.filter(collection => {
    const matchesSearch = collection.name.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesStatus = statusFilter === 'All Status' || collection.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  useEffect(() => {
    fetchCollections(pagination.currentPage);
  }, [fetchCollections, pagination.currentPage, refreshTrigger]);

  // Debounced search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (pagination.currentPage === 1) {
        fetchCollections(1);
      } else {
        setPagination(prev => ({ ...prev, currentPage: 1 }));
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery, statusFilter, fetchCollections]);

  if (loading && collections.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#55000C]"></div>
        <span className="ml-3 text-gray-600">Loading collections...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Collections Section */}
      <div className="bg-white rounded-lg border border-gray-200">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h2 className="text-xl font-medium text-[#0E1628]">All Collections</h2>
          
          <div className="flex gap-4 items-center">
            {/* Search Box */}
            <div className="relative">
              <div className="absolute left-3 top-1/2 transform -translate-y-1/2">
                <SearchIcon size={20} color="#6B635D" />
              </div>
              <input
                type="text"
                placeholder="Search collections..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="p-2 pl-10 pr-3 border w-52 border-[#DBDBDB] bg-transparent placeholder:text-[#6B635D] placeholder:text-sm focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent"
              />
            </div>

            {/* Status Filter */}
            <div className="relative">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="p-2 pl-3 pr-8 border w-40 border-[#DBDBDB] bg-transparent text-[#1C2A46] text-sm appearance-none focus:outline-none focus:ring-1 focus:ring-[#D4A574] focus:border-transparent"
              >
                {statusOptions.map((option) => (
                  <option key={option} value={option}>
                    {option}
                  </option>
                ))}
              </select>
              <div className="absolute right-2 top-1/2 transform -translate-y-1/2 pointer-events-none">
                <ArrowDown size={16} color="#6B635D" />
              </div>
            </div>
          </div>
        </div>

        {/* Collections Table */}
{/* Collections Table */}
<div className="overflow-x-auto">
  <table className="min-w-full divide-y divide-gray-200">
    <thead className="bg-[#F3F4F6]">
      <tr>
        <th className="px-6 py-3 text-left text-xs font-normal text-[#6B635D] uppercase tracking-wider">
          Name
        </th>
        <th className="px-6 py-3 text-left text-xs font-normal text-[#6B635D] uppercase tracking-wider">
          Inventory
        </th>
        <th className="px-6 py-3 text-left text-xs font-normal text-[#6B635D] uppercase tracking-wider">
          Collection Type
        </th>
        <th className="px-6 py-3 text-left text-xs font-normal text-[#6B635D] uppercase tracking-wider">
          Price
        </th>
        <th className="px-6 py-3 text-left text-xs font-normal text-[#6B635D] uppercase tracking-wider">
          Status
        </th>
        <th className="px-6 py-3 text-left text-xs font-normal text-[#6B635D] uppercase tracking-wider">
          Action
        </th>
      </tr>
    </thead>
    <tbody className="bg-white divide-y divide-gray-200">
      {collections.map((collection) => (
        <CollectionItem
          key={collection.id}
          collection={collection}
          onToggleExpand={handleToggleExpand}
          onSubToggleExpand={handleSubToggleExpand}
          onCollectionAction={handleCollectionAction}
          depth={0}
        />
      ))}
    </tbody>
  </table>
</div>

        {/* Pagination */}
        {pagination.totalPages > 1 && (
          <div className="px-6 py-4 border-t border-gray-200">
            <Pagination
              currentPage={pagination.currentPage}
              totalPages={pagination.totalPages}
              totalItems={pagination.totalItems}
              itemsPerPage={pagination.itemsPerPage}
              onPageChange={handlePageChange}
            />
          </div>
        )}

        {/* Empty State */}
        {collections.length === 0 && !loading && (
          <div className="px-6 py-12 text-center">
            <div className="text-gray-400 mb-2">No collections found</div>
            <div className="text-sm text-gray-500">
              {searchQuery || statusFilter !== 'All Status' 
                ? 'Try adjusting your search or filter'
                : 'Add your first collection to get started'}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default CollectionsTab;